import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import FinishDialog from "@/components/FinishDialog";
import { HardHat, CheckCircle2, Clock, FolderKanban, Calendar as CalendarIcon, AlertCircle, Zap, ClipboardCheck, Play, History, Timer } from "lucide-react";
import { toast } from "sonner";
import { formatDuration } from "@/lib/format";
import { useNowTick } from "@/lib/useNowTick";
import AttachmentsSection from "@/components/AttachmentsSection";

function fmtDate(s) {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("tr-TR"); } catch { return s; }
}
function isDateOverdue(d) {
  if (!d) return false;
  const today = new Date(); today.setHours(0,0,0,0);
  return new Date(d) < today;
}

const PRIORITY_LABEL = { normal: "Normal", high: "Yüksek", urgent: "Acil" };
const PRIORITY_CLS = {
  normal: "bg-zinc-100 text-zinc-600 border-zinc-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
};

function AssignmentCard({ a, onOpen, onStart }) {
  const dates = a.scheduled_dates || [];
  const overdue = a.overdue;
  const cardCls = a.priority === "urgent"
    ? "border-red-300 bg-red-50/30"
    : a.priority === "high"
      ? "border-orange-300 bg-orange-50/20"
      : overdue
        ? "border-red-200 bg-red-50/20"
        : "border-zinc-200";
  return (
    <Card
      className={`p-4 border shadow-none cursor-pointer hover:shadow transition ${cardCls}`}
      data-testid={`assignment-card-${a.id}`}
      onClick={() => onOpen(a)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-zinc-500 flex items-center gap-1">
            <FolderKanban size={12} />
            <span className="truncate">{a.project_name || "Proje"}</span>
            <span className="text-zinc-400">·</span>
            <span className="truncate">{a.customer_name}</span>
          </div>
          <div className="font-semibold mt-1 truncate">{a.item_name}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <HardHat size={14} className="text-amber-600" />
            <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">{a.stage}</span>
            <span className={`inline-block px-2 py-0.5 rounded text-xs border ${PRIORITY_CLS[a.priority] || PRIORITY_CLS.normal}`}>
              {a.priority === "urgent" && <Zap size={10} className="inline mr-0.5"/>}
              {PRIORITY_LABEL[a.priority] || "Normal"}
            </span>
            {overdue && (
              <span className="inline-block px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 border border-red-300">
                <AlertCircle size={10} className="inline mr-0.5"/>Gecikmiş
              </span>
            )}
          </div>
          {dates.length > 0 && (
            <div className="text-xs text-zinc-600 mt-2 flex items-center gap-1 flex-wrap">
              <CalendarIcon size={11} />
              {dates.map((d, i) => (
                <span key={d} className={isDateOverdue(d) ? "text-red-700 font-medium" : ""}>
                  {d}{i < dates.length - 1 && <span className="text-zinc-400"> · </span>}
                </span>
              ))}
            </div>
          )}
          {a.notes && (
            <div className="text-xs text-zinc-600 mt-2 whitespace-pre-wrap bg-white/60 rounded p-2 border border-zinc-100 line-clamp-2">
              {a.notes}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-200/70" onClick={(e) => e.stopPropagation()}>
        <Button
          onClick={() => onStart(a)}
          className="w-full bg-zinc-900 hover:bg-zinc-800"
          size="sm"
          data-testid={`card-start-${a.id}`}
        >
          <Play size={14} className="mr-1" />İşe Başla
        </Button>
      </div>
    </Card>
  );
}

function ActiveJobCard({ log, onOpen, onFinish }) {
  const duration = formatDuration(log.started_at, null);
  return (
    <Card
      className="p-4 border border-zinc-200 shadow-none cursor-pointer hover:shadow transition"
      data-testid={`active-job-${log.id}`}
      onClick={() => onOpen(log)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-zinc-500 flex items-center gap-1">
            <FolderKanban size={12} />
            <span className="truncate">{log.project?.name || "Proje"}</span>
            <span className="text-zinc-400">·</span>
            <span className="truncate">{log.project?.customer_name}</span>
          </div>
          <div className="font-semibold mt-1 truncate">{log.item?.name || "Ürün"}</div>
          <div className="text-sm text-zinc-600 mt-1">
            {[log.item?.material, log.item?.color, log.item?.model].filter(Boolean).join(" · ") || "-"}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <HardHat size={14} className="text-amber-600" />
            <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
              {log.stage}
            </span>
            <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 border border-blue-300">
              <Play size={10} className="inline mr-0.5"/>Devam ediyor
            </span>
            <span
              className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-900 text-white font-medium"
              data-testid={`active-duration-${log.id}`}
              title={`Başlangıç: ${fmtDate(log.started_at)}`}
            >
              <Timer size={10} className="inline mr-0.5"/>{duration}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-2">Başlangıç: {fmtDate(log.started_at)}</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-200/70" onClick={(e) => e.stopPropagation()}>
        <Button
          onClick={() => onFinish(log)}
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          size="sm"
          data-testid={`active-finish-${log.id}`}
        >
          <CheckCircle2 size={14} className="mr-1" />İş Tamamlandı
        </Button>
      </div>
    </Card>
  );
}

export default function MyJobs() {
  useNowTick(60); // re-render every minute for live durations
  const [data, setData] = useState({ active: [], recent: [], assignments: [] });
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);

  // Detail modal — supports both assignment and active-log entries
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailKind, setDetailKind] = useState(null); // "assignment" | "active"
  const [detailData, setDetailData] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);

  // Finish dialog
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishTarget, setFinishTarget] = useState(null); // { projectId, itemId, itemName }

  const load = () => api.get("/my/jobs").then(r => setData(r.data));
  useEffect(() => {
    Promise.all([
      load(),
      api.get("/settings").then(r => setStages(r.data.manufacturing_stages || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const openDetailForAssignment = async (a) => {
    setDetailKind("assignment");
    setDetailData(a);
    setDetailOpen(true);
    try {
      const r = await api.get(`/projects/${a.project_id}/items/${a.item_id}/logs`);
      setDetailLogs(r.data);
    } catch { setDetailLogs([]); }
  };

  const openDetailForActive = async (log) => {
    setDetailKind("active");
    setDetailData(log);
    setDetailOpen(true);
    try {
      const r = await api.get(`/projects/${log.project?.id}/items/${log.item_id}/logs`);
      setDetailLogs(r.data);
    } catch { setDetailLogs([]); }
  };

  const startWork = async (a) => {
    try {
      await api.post(`/projects/${a.project_id}/items/${a.item_id}/takeover`, {
        stage: a.stage,
        assignment_id: a.id,
      });
      toast.success(`"${a.stage}" aşaması başlatıldı`);
      setDetailOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Başlatılamadı");
    }
  };

  const finishActiveLog = (log) => {
    setFinishTarget({
      projectId: log.project?.id,
      itemId: log.item_id,
      itemName: log.item?.name || "Ürün",
    });
    setFinishOpen(true);
    setDetailOpen(false);
  };

  if (loading) return <div className="text-zinc-500">Yükleniyor...</div>;

  // ONLY show assignments that haven't been started yet (no active_log)
  const assignments = (data.assignments || []).filter(a => !a.active_log);
  const priorityAssignments = assignments.filter(a => a.priority === "urgent" || a.priority === "high");
  const overdueAssignments = assignments.filter(a => a.priority === "normal" && a.overdue);
  const upcomingAssignments = assignments.filter(a => a.priority === "normal" && !a.overdue);

  // Detail dialog derivation
  const isAssignmentDetail = detailKind === "assignment";
  const detailAssignment = isAssignmentDetail ? detailData : null;
  const detailActive = !isAssignmentDetail ? detailData : null;
  const detailItem = detailAssignment?.item || detailActive?.item || {};
  const detailProject = detailAssignment
    ? { name: detailAssignment.project_name, customer: detailAssignment.customer_name, id: detailAssignment.project_id }
    : { name: detailActive?.project?.name, customer: detailActive?.project?.customer_name, id: detailActive?.project?.id };

  return (
    <div className="space-y-8" data-testid="my-jobs-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500">İmalat</div>
        <h1 className="text-3xl mt-1">İşlerim</h1>
        <p className="text-sm text-zinc-500 mt-1">Size atanan görevler, devraldığınız aşamalar ve son tamamlanan işleriniz.</p>
      </div>

      <section>
        <h2 className="text-base font-semibold text-zinc-800 mb-3 flex items-center gap-2">
          <ClipboardCheck size={16} className="text-blue-600" /> Size Atanan Görevler ({assignments.length})
        </h2>
        {assignments.length === 0 ? (
          <Card className="p-6 border border-zinc-200 shadow-none text-center text-zinc-500 text-sm">
            Şu anda size atanmış — başlatılmamış — bir görev bulunmuyor.
          </Card>
        ) : (
          <div className="space-y-4">
            {priorityAssignments.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-red-700 mb-2 flex items-center gap-1">
                  <Zap size={12}/>Öncelikli
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {priorityAssignments.map(a => <AssignmentCard key={a.id} a={a} onOpen={openDetailForAssignment} onStart={startWork} />)}
                </div>
              </div>
            )}
            {overdueAssignments.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-red-600 mb-2 flex items-center gap-1">
                  <AlertCircle size={12}/>Gecikmiş
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {overdueAssignments.map(a => <AssignmentCard key={a.id} a={a} onOpen={openDetailForAssignment} onStart={startWork} />)}
                </div>
              </div>
            )}
            {upcomingAssignments.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <CalendarIcon size={12}/>Planlı
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {upcomingAssignments.map(a => <AssignmentCard key={a.id} a={a} onOpen={openDetailForAssignment} onStart={startWork} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-base font-semibold text-zinc-800 mb-3 flex items-center gap-2">
          <Clock size={16} className="text-amber-600" /> Aktif İşleriniz ({data.active.length})
        </h2>
        {data.active.length === 0 ? (
          <Card className="p-6 border border-zinc-200 shadow-none text-center text-zinc-500 text-sm">
            Şu anda devralınmış aktif bir işiniz yok.
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {data.active.map(l => (
              <ActiveJobCard key={l.id} log={l} onOpen={openDetailForActive} onFinish={finishActiveLog} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-base font-semibold text-zinc-800 mb-3 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-600" /> Son Tamamlanan İşleriniz
        </h2>
        {data.recent.length === 0 ? (
          <Card className="p-6 border border-zinc-200 shadow-none text-center text-zinc-500 text-sm">
            Henüz tamamlanmış bir işleminiz bulunmuyor.
          </Card>
        ) : (
          <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
            <table className="acc">
              <thead>
                <tr><th>Proje</th><th>Ürün</th><th>Aşama</th><th>Başlangıç</th><th>Bitiş</th><th>Süre</th><th>Durum</th></tr>
              </thead>
              <tbody>
                {data.recent.map(l => (
                  <tr key={l.id} data-testid={`recent-job-${l.id}`}>
                    <td className="text-zinc-700">{l.project?.name || "-"}</td>
                    <td className="font-medium">{l.item?.name || "-"}</td>
                    <td>{l.stage}</td>
                    <td className="text-zinc-500 text-sm">{fmtDate(l.started_at)}</td>
                    <td className="text-zinc-500 text-sm">{fmtDate(l.finished_at)}</td>
                    <td className="text-zinc-700 text-sm" data-testid={`recent-duration-${l.id}`}>
                      {formatDuration(l.started_at, l.finished_at)}
                    </td>
                    <td>
                      {l.completed
                        ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">Ürünle işim bitti</span>
                        : <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">Yeni aşamaya geçti</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Unified detail modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid={isAssignmentDetail ? "assignment-detail-dialog" : "active-detail-dialog"} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isAssignmentDetail ? "Görev Detayı" : "Aktif İş Detayı"}</DialogTitle>
            <DialogDescription>
              {detailData && (
                <>
                  <span className="font-medium text-zinc-800">{detailItem?.name || (isAssignmentDetail ? detailAssignment.item_name : "Ürün")}</span>
                  {" — "}
                  <span>{detailProject.name}</span> · <span>{detailProject.customer}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                  <HardHat size={11} className="inline mr-0.5" /> {isAssignmentDetail ? detailAssignment.stage : detailActive.stage}
                </span>
                {isAssignmentDetail && (
                  <span className={`inline-block px-2 py-0.5 rounded text-xs border ${PRIORITY_CLS[detailAssignment.priority] || PRIORITY_CLS.normal}`}>
                    {detailAssignment.priority === "urgent" && <Zap size={10} className="inline mr-0.5"/>}
                    {PRIORITY_LABEL[detailAssignment.priority] || "Normal"}
                  </span>
                )}
                {isAssignmentDetail && detailAssignment.overdue && (
                  <span className="inline-block px-2 py-0.5 rounded text-xs bg-red-100 text-red-800 border border-red-300">
                    <AlertCircle size={10} className="inline mr-0.5"/>Gecikmiş
                  </span>
                )}
                {!isAssignmentDetail && (
                  <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 border border-blue-300">
                    <Play size={10} className="inline mr-0.5"/>Devam ediyor — {fmtDate(detailActive.started_at)}
                  </span>
                )}
                {!isAssignmentDetail && (
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-900 text-white font-medium"
                    data-testid="active-detail-duration"
                  >
                    <Timer size={10} className="inline mr-0.5"/>{formatDuration(detailActive.started_at, null)}
                  </span>
                )}
              </div>

              {isAssignmentDetail && (detailAssignment.scheduled_dates || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Planlanan Tarih(ler)</div>
                  <div className="flex flex-wrap gap-2 text-sm">
                    {detailAssignment.scheduled_dates.map(d => (
                      <span key={d} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${isDateOverdue(d) ? "bg-red-50 text-red-700 border-red-200" : "bg-zinc-50 border-zinc-200 text-zinc-700"}`}>
                        <CalendarIcon size={11} /> {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isAssignmentDetail && detailAssignment.notes && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Görev Notları</div>
                  <div className="text-sm whitespace-pre-wrap bg-zinc-50 rounded p-2 border border-zinc-200">{detailAssignment.notes}</div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Malzeme</div>
                  <div className="mt-0.5">{detailItem?.material || <span className="text-zinc-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Renk</div>
                  <div className="mt-0.5">{detailItem?.color || <span className="text-zinc-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Model</div>
                  <div className="mt-0.5">{detailItem?.model || <span className="text-zinc-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Miktar</div>
                  <div className="mt-0.5">{Number(detailItem?.quantity || 0)} {detailItem?.unit || ""}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Ürün Notları</div>
                  <div className="mt-0.5 text-sm whitespace-pre-wrap">{detailItem?.notes || <span className="text-zinc-400">—</span>}</div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <History size={12} /> İşlem Geçmişi ({detailLogs.length})
                </div>
                {detailLogs.length === 0 ? (
                  <div className="text-sm text-zinc-500">Henüz kayıt yok.</div>
                ) : (
                  <div className="max-h-40 overflow-auto rounded-md border border-zinc-200">
                    <table className="acc">
                      <thead><tr><th>Usta</th><th>Aşama</th><th>Başlangıç</th><th>Bitiş</th><th>Süre</th></tr></thead>
                      <tbody>
                        {detailLogs.map(l => (
                          <tr key={l.id}>
                            <td className="font-medium">{l.user_name}</td>
                            <td><span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{l.stage}</span></td>
                            <td className="text-xs text-zinc-600">{fmtDate(l.started_at)}</td>
                            <td className="text-xs text-zinc-600">{fmtDate(l.finished_at)}</td>
                            <td className="text-xs" data-testid={`log-duration-${l.id}`}>
                              {l.finished_at
                                ? <span className="text-zinc-700">{formatDuration(l.started_at, l.finished_at)}</span>
                                : <span className="text-blue-700 font-medium">{formatDuration(l.started_at, null)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <AttachmentsSection
                ownerKind="project_item"
                ownerId={detailData.item_id}
                title="Fotoğraf & Dosyalar"
                accept="image/*,application/pdf"
                readOnly={isAssignmentDetail}
                testIdPrefix="job-item-attachments"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setDetailOpen(false)}>Kapat</Button>
            {isAssignmentDetail && detailAssignment && (
              <Button
                onClick={() => startWork(detailAssignment)}
                className="bg-zinc-900 hover:bg-zinc-800"
                data-testid="detail-start-button"
              ><Play size={14} className="mr-1" />İşe Başla</Button>
            )}
            {!isAssignmentDetail && detailActive && (
              <Button
                onClick={() => finishActiveLog(detailActive)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="detail-finish-button"
              ><CheckCircle2 size={14} className="mr-1" />İş Tamamlandı</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FinishDialog
        open={finishOpen}
        onOpenChange={setFinishOpen}
        projectId={finishTarget?.projectId}
        itemId={finishTarget?.itemId}
        itemName={finishTarget?.itemName}
        stages={stages}
        onFinished={load}
      />
    </div>
  );
}
